// src/modals/OnboardingModal.jsx
// ============================================================
// ONBOARDING MODAL - First-time user brewery creation
// ============================================================
// Shown when user logs in with no bars
// Prompts them to create their first free brewery

import React, { useState } from 'react';
import { Loader, AlertCircle } from 'lucide-react';

export const OnboardingModal = ({ show, onComplete, isLoading = false }) => {
  const [breweryName, setBreweryName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!breweryName.trim()) {
      setError('Please enter a brewery name');
      return;
    }

    if (breweryName.trim().length < 2) {
      setError('Brewery name must be at least 2 characters');
      return;
    }

    await onComplete(breweryName.trim());
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-8 text-center">
        {/* Logo */}
        <img src="/kegops-logo.png" alt="KegOps" className="h-16 mx-auto mb-6" />

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome to KegOps!
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          Let's get your first brewery set up. Start with our free plan and upgrade anytime.
        </p>

        {/* Free Plan Benefits */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6 text-left">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
            🎉 Free Plan Includes:
          </p>
          <ul className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
            <li>✓ Up to 2 kegs</li>
            <li>✓ 1 location</li>
            <li>✓ 2 sublocations</li>
            <li>✓ Up to 2 team members</li>
          </ul>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-4 flex gap-2 text-left">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
              Brewery Name
            </label>
            <input
              type="text"
              value={breweryName}
              onChange={(e) => setBreweryName(e.target.value)}
              placeholder="e.g., Local Craft Brewery"
              maxLength={50}
              disabled={isLoading}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !breweryName.trim()}
            className="w-full px-4 py-3 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white rounded-lg font-semibold transition flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Creating Brewery...
              </>
            ) : (
              'Create My First Brewery'
            )}
          </button>
        </form>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-6">
          You can add more breweries and upgrade your plan later in Settings.
        </p>
      </div>
    </div>
  );
};

export default OnboardingModal;
